import { FormatNumberOptions, useIntl } from "react-intl";
import { shallowEqual, useSelector } from "react-redux";
import { formatAmountForDisplay } from "app/utils/utils";

export type UseFormattedAmountOptions = {
	/** Raw amount to format. */
	amount: number;
	/** Overrides the store currency */
	currencyCode?: string;
	/** Overrides the store shop, used for per-currency display fixes. */
	shop?: string;
} & FormatNumberOptions;

type CurrencyState = {
	currency?: string;
	shop?: string;
};

/**
 * Formats a raw amount into a localized currency string.
 *
 * Currency and shop default to the store and can be overridden. Any extra
 * `Intl.NumberFormatOptions` (`notation`, `signDisplay`, …) are forwarded to
 * `formatNumber`. Returns `null` when the amount is not a finite number or no
 * currency is available, so the caller can skip rendering.
 */
export const useFormattedAmount = ({
	amount,
	currencyCode,
	shop,
	minimumFractionDigits = 0,
	maximumFractionDigits = 2,
	...rest
}: UseFormattedAmountOptions): string | null => {
	const { formatNumber } = useIntl();
	const { currency: storeCurrency, shop: storeShop } = useSelector(
		(state: CurrencyState) => ({ currency: state.currency, shop: state.shop }),
		shallowEqual
	);

	const resolvedCurrency = currencyCode ?? storeCurrency;
	const resolvedShop = shop ?? storeShop;

	if (!Number.isFinite(amount) || !resolvedCurrency) {
		return null;
	}

	const formatted = formatNumber(amount, {
		style: "currency",
		currency: resolvedCurrency,
		currencyDisplay: "symbol",
		minimumFractionDigits,
		maximumFractionDigits,
		...rest,
	});

	return formatAmountForDisplay(formatted, resolvedCurrency, resolvedShop ?? "");
};
